<template>
  <div class="invite-container">
    <!-- Loading State -->
    <div v-if="loading || isProcessing" class="invite-splash" role="status">
      <div class="invite-splash__content">
        <img
          src="/images/project-blue-logo.png"
          alt="Project Blue"
          class="invite-splash__logo"
          width="200"
          height="auto"
        />
        <p class="invite-splash__message">{{ splashMessage }}</p>
        <div class="invite-splash__progress-track">
          <div class="invite-splash__progress-fill" />
        </div>
      </div>
    </div>

    <!-- Main Invite Card -->
    <div v-else class="invite-card-wrapper">
      <div class="invite-card">
        <div class="text-center mb-4">
          <img
            src="/images/project-blue-logo.png"
            alt="Project Blue"
            class="invite-card__logo"
            width="180"
            height="auto"
          />
          <h1 class="invite-card__title">You're Invited!</h1>
        </div>

        <!-- Error Box -->
        <div v-if="error" class="invite-error-box">
          <div class="invite-error-content">
            <svg class="invite-error-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
            <div class="invite-error-text">
              <h3>{{ errorTitle }}</h3>
              <p>{{ error }}</p>
              <p class="invite-support-link">
                Need help? Contact
                <a href="mailto:support@projectblue.com">support@projectblue.com</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Valid Invite Content -->
        <div v-else class="invite-details">
          <div class="invite-info-row">
            <span class="invite-label">Invited Email:</span>
            <span class="invite-value">{{ inviteData?.email }}</span>
          </div>
          <p class="invite-validity-note">
            This invitation link is valid for 30 days. Click below to create your Project Blue account.
          </p>

          <button
            class="btn btn-primary invite-action-btn"
            :disabled="isProcessing || !canProceedToSignup"
            @click="startSignup"
          >
            {{ isProcessing ? "Redirecting..." : "Continue to Sign Up" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import { loginWithAuth0 } from "@/composables/auth0";
import { AUTHENTICATION_URL } from "@/utils/constants";
import {
  AUTH_STORAGE_KEYS,
  AUTH_FAILURE_REASONS,
  HTTP_STATUS,
} from "@/constants/auth";

const route = useRoute();

const inviteData = ref(null);
const error = ref(null);
const errorTitle = ref("Invitation Notice");
const loading = ref(true);
const isProcessing = ref(false);
const token = ref(null);

const splashMessage = computed(() =>
  isProcessing.value ? "Redirecting to sign up..." : "Validating your invite..."
);

const canProceedToSignup = computed(() => {
  return inviteData.value && !error.value;
});

const validateInviteToken = async (inviteToken) => {
  try {
    // Base may or may not end with "/" (pre-prod/prod use ".../api"), so join with exactly one.
    const url = `${AUTHENTICATION_URL.replace(/\/+$/, "")}/User/ValidateInvite`;
    const response = await axios.get(url, {
      params: { token: inviteToken },
      timeout: 10000,
    });

    if (response.status === HTTP_STATUS.OK && response.data) {
      if (response.data.error) {
        errorTitle.value = "Invitation Invalid or Expired";
        error.value = response.data.error;
      } else {
        inviteData.value = response.data;
        // Save invite data in localStorage
        localStorage.setItem(
          AUTH_STORAGE_KEYS.INVITE_DATA,
          JSON.stringify(response.data)
        );
        localStorage.setItem(AUTH_STORAGE_KEYS.INVITE_TOKEN, inviteToken);
      }
    } else {
      errorTitle.value = "Invitation Invalid";
      error.value = "The invitation link is invalid or has already been used.";
    }
  } catch (err) {
    console.error("Error validating invite token:", err);
    errorTitle.value = "Validation Error";
    if (err.response?.status === HTTP_STATUS.NOT_FOUND) {
      error.value = "This invitation does not exist or has expired.";
    } else {
      error.value = "Unable to validate invitation. Please try again or contact support.";
    }
  } finally {
    loading.value = false;
  }
};

const startSignup = async () => {
  if (!token.value || !inviteData.value?.email) {
    error.value = "Missing required invite information.";
    return;
  }

  isProcessing.value = true;
  try {
    // Preserve invite details in localStorage for callback verification
    localStorage.setItem(AUTH_STORAGE_KEYS.INVITE_TOKEN, token.value);
    localStorage.setItem(
      AUTH_STORAGE_KEYS.INVITE_DATA,
      JSON.stringify(inviteData.value)
    );

    // Redirect to Auth0 signup screen with email pre-filled
    await loginWithAuth0({
      screen_hint: "signup",
      login_hint: inviteData.value.email,
      prompt: "login",
    });
  } catch (err) {
    console.error("Failed to start signup:", err);
    errorTitle.value = "Signup Error";
    error.value = "Could not redirect to sign up. Please try again.";
    isProcessing.value = false;
  }
};

onMounted(async () => {
  // Extract token from query parameters (handles hash and search params)
  const searchParams = new URLSearchParams(window.location.search);
  token.value = route.query.token || searchParams.get("token");

  // Check if returning with error
  const auth0Error = route.query.error || searchParams.get("error");
  if (
    auth0Error === AUTH_FAILURE_REASONS.ORGANIZATION_RESTRICTION ||
    auth0Error === "organization_restriction"
  ) {
    errorTitle.value = "Organization Restriction";
    error.value =
      "Self-service signup is restricted for this organization. Please contact your administrator.";
    loading.value = false;
    return;
  }

  if (!token.value) {
    // Check if token was previously saved
    const savedToken = localStorage.getItem(AUTH_STORAGE_KEYS.INVITE_TOKEN);
    if (savedToken) {
      token.value = savedToken;
    } else {
      errorTitle.value = "Missing Token";
      error.value = "No invitation token was found in the link.";
      loading.value = false;
      return;
    }
  }

  await validateInviteToken(token.value);
});
</script>

<style scoped>
.invite-container {
  min-height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  font-family: "Gilroy", sans-serif;
}

.invite-splash {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}

.invite-splash__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  padding: 2rem;
}

.invite-splash__logo {
  max-width: 180px;
  margin-bottom: 1.5rem;
}

.invite-splash__message {
  font-size: 16px;
  color: #333;
  margin-bottom: 1.25rem;
}

.invite-splash__progress-track {
  width: 240px;
  height: 6px;
  background-color: rgba(33, 60, 229, 0.15);
  border-radius: 3px;
  overflow: hidden;
}

.invite-splash__progress-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(to right, #213ce5, #2380f2);
  border-radius: 3px;
  animation: invite-progress 1.5s ease-in-out infinite;
}

@keyframes invite-progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

.invite-card-wrapper {
  padding: 1.5rem;
  width: 100%;
  max-width: 460px;
}

.invite-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  padding: 2.5rem 2rem;
  border: 1px solid #e2e8f0;
}

.invite-card__logo {
  margin-bottom: 1rem;
}

.invite-card__title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.invite-error-box {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.invite-error-content {
  display: flex;
  align-items: flex-start;
}

.invite-error-icon {
  width: 24px;
  height: 24px;
  color: #ef4444;
  flex-shrink: 0;
  margin-right: 0.75rem;
}

.invite-error-text h3 {
  font-size: 16px;
  font-weight: 600;
  color: #991b1b;
  margin: 0 0 0.25rem;
}

.invite-error-text p {
  font-size: 14px;
  color: #7f1d1d;
  margin: 0;
}

.invite-support-link {
  margin-top: 0.5rem !important;
  font-size: 13px !important;
}

.invite-support-link a {
  color: #2563eb;
  text-decoration: underline;
}

.invite-details {
  margin-top: 1.5rem;
}

.invite-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8fafc;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.invite-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.invite-value {
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
}

.invite-validity-note {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 1.5rem;
  text-align: center;
}

.invite-action-btn {
  width: 100%;
  padding: 0.75rem;
  font-size: 15px;
  font-weight: 600;
  background: linear-gradient(to right, #213ce5, #2380f2);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  transition: opacity 0.2s;
}

.invite-action-btn:hover {
  opacity: 0.9;
}

.invite-action-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}
</style>
